import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { draggable, dropTargetForElements, monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { attachClosestEdge, extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import IconButton from '@material-ui/core/IconButton';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import AddIcon from '@material-ui/icons/Add';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import EditIcon from '@material-ui/icons/Edit';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import Tooltip from '@material-ui/core/Tooltip';
import { fetchDataDetail } from '@actions/data-detail-actions';
import { saveDataType } from '@actions/data-types-actions';
import { pushApplicationMessage } from '@actions/messages-actions';
import * as colors from '@constants/colors';
import SwitchControl from '@shared/form/switch-control';
import './entity-type-form-builder.scss';

const FIELD_TYPES = [
  { value: 'CharField', label: 'Single-line Text' },
  { value: 'TextField', label: 'Multi-line Text' },
  { value: 'IntegerField', label: 'Number' },
  { value: 'FloatField', label: 'Decimal' },
  { value: 'BooleanField', label: 'Yes / No' },
  { value: 'DateField', label: 'Date' },
  { value: 'DateTimeField', label: 'Date & Time' }
];

const FIELD_WIDTHS = [
  { value: 'full', label: 'Full width' },
  { value: 'half', label: 'Half width' }
];

// ─── Field Input Preview ─────────────────────────────────────────────────────

const FieldInputPreview = ({ field }) => {
  switch (field.type) {
  case 'TextField':
    return <div styleName="preview-textarea" />;
  case 'DateField':
  case 'DateTimeField':
    return (
      <div styleName="preview-input preview-date-input">
        <span styleName="preview-placeholder">mm/dd/yyyy</span>
        <CalendarTodayIcon style={{ fontSize: '1rem', color: colors.dotmapsGray }} />
      </div>
    );
  case 'BooleanField':
    return (
      <div styleName="preview-input" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <div styleName="preview-checkbox" />
      </div>
    );
  case 'SelectField':
    return (
      <div styleName="preview-input preview-select-input">
        <span styleName="preview-placeholder" />
        <ExpandMoreIcon style={{ fontSize: '1rem', color: colors.dotmapsGray }} />
      </div>
    );
  default:
    return <div styleName="preview-input" />;
  }
};

FieldInputPreview.propTypes = { field: PropTypes.object };

// ─── Field Preview Card ──────────────────────────────────────────────────────

const FieldPreviewCard = ({ field, fieldName, index, sectionId, onFieldClick }) => {
  const [hovered, setHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [closestEdge, setClosestEdge] = useState(null);
  const cardRef = useRef(null);
  const dragHandleRef = useRef(null);

  useEffect(() => {
    const element = cardRef.current;
    const handle = dragHandleRef.current;
    if (!element || !handle) {
      return () => {};
    }
    return draggable({
      element,
      dragHandle: handle,
      getInitialData: () => ({ index, fieldName, sectionId, type: 'entity-field' }),
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false)
    });
  }, [index, fieldName, sectionId]);

  useEffect(() => {
    const element = cardRef.current;
    if (!element) {
      return () => {};
    }
    return dropTargetForElements({
      element,
      canDrop: ({ source }) => source.data.type === 'entity-field' && source.data.sectionId === sectionId,
      getData: ({ input, element: el }) => attachClosestEdge({ index, sectionId }, {
        input, element: el, allowedEdges: ['top', 'bottom']
      }),
      onDragEnter: ({ self }) => {
        setIsDraggedOver(true); setClosestEdge(extractClosestEdge(self.data)); 
      },
      onDrag: ({ self }) => setClosestEdge(extractClosestEdge(self.data)),
      onDragLeave: () => {
        setIsDraggedOver(false); setClosestEdge(null); 
      },
      onDrop: () => {
        setIsDraggedOver(false); setClosestEdge(null); 
      }
    });
  }, [index, sectionId]);

  const isFullWidth = !field.width || field.width === 'full';
  const label = field.label || fieldName;

  return (
    <div
      ref={cardRef}
      styleName={`field-card${isFullWidth ? ' field-card-full' : ' field-card-half'}`}
      style={{ opacity: isDragging ? 0.4 : 1, position: 'relative' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {isDraggedOver && closestEdge === 'top' && <div styleName="drop-line" style={{ top: 0 }} />}
      {isDraggedOver && closestEdge === 'bottom' && <div styleName="drop-line" style={{ bottom: 0 }} />}
      <div styleName="field-card-header">
        <span styleName="field-label">
          {label}
          {field.required && <span styleName="required-star"> *</span>}
        </span>
        <div styleName="field-card-actions" style={{ opacity: hovered ? 1 : 0 }}>
          <div
            ref={dragHandleRef}
            styleName="drag-handle"
            onClick={ev => ev.stopPropagation()}
            role="button"
            tabIndex={-1}
          >
            <DragIndicatorIcon style={{ fontSize: '1rem', color: '#999' }} />
          </div>
          <IconButton
            size="small"
            onClick={() => onFieldClick(fieldName, sectionId)}
          >
            <EditIcon style={{ fontSize: '0.875rem', color: colors.dotmapsGray }} />
          </IconButton>
          <IconButton size="small">
            <MoreVertIcon style={{ fontSize: '0.875rem', color: colors.dotmapsGray }} />
          </IconButton>
        </div>
      </div>
      <FieldInputPreview field={field} />
    </div>
  );
};

FieldPreviewCard.propTypes = {
  field: PropTypes.object,
  fieldName: PropTypes.string,
  index: PropTypes.number,
  onFieldClick: PropTypes.func,
  sectionId: PropTypes.string
};

// ─── Section Card ────────────────────────────────────────────────────────────

const SectionCard = ({
  activeSectionId, fields, index, section,
  onAddField, onDeleteSection, onFieldClick, onRenameSectionStart, onToggle
}) => {
  const isActive = section.id === activeSectionId;
  const cardRef = useRef(null);
  const dragHandleRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [closestEdge, setClosestEdge] = useState(null);

  useEffect(() => {
    const element = cardRef.current;
    const handle = dragHandleRef.current;
    if (!element || !handle) {
      return () => {};
    }
    return draggable({
      element,
      dragHandle: handle,
      getInitialData: () => ({ index, sectionId: section.id, type: 'entity-section' }),
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false)
    });
  }, [index, section.id]);

  useEffect(() => {
    const element = cardRef.current;
    if (!element) {
      return () => {};
    }
    return dropTargetForElements({
      element,
      canDrop: ({ source }) => source.data.type === 'entity-section',
      getData: ({ input, element: el }) => attachClosestEdge({ index, sectionId: section.id }, {
        input, element: el, allowedEdges: ['top', 'bottom']
      }),
      onDragEnter: ({ self }) => {
        setIsDraggedOver(true); setClosestEdge(extractClosestEdge(self.data)); 
      },
      onDrag: ({ self }) => setClosestEdge(extractClosestEdge(self.data)),
      onDragLeave: () => {
        setIsDraggedOver(false); setClosestEdge(null); 
      },
      onDrop: () => {
        setIsDraggedOver(false); setClosestEdge(null); 
      }
    });
  }, [index, section.id]);

  const sectionFields = (section.fields || []).map(key => ({ key, field: fields[key] })).filter(({ field }) => Boolean(field));

  return (
    <div
      ref={cardRef}
      styleName={`section-card${isActive ? ' section-card-active' : ''}${isDragging ? ' section-card-dragging' : ''}`}
      style={{ position: 'relative' }}
    >
      {isDraggedOver && closestEdge === 'top' && <div styleName="section-drop-line" style={{ top: -1 }} />}
      {isDraggedOver && closestEdge === 'bottom' && <div styleName="section-drop-line" style={{ bottom: -1 }} />}
      <div styleName="section-header">
        <div
          ref={dragHandleRef}
          styleName="section-drag-handle"
          role="button"
          tabIndex={-1}
          onClick={ev => ev.stopPropagation()}
        >
          <DragIndicatorIcon style={{ fontSize: '1rem', color: '#bbb' }} />
        </div>
        <span styleName="section-name">{section.name}</span>
        <div styleName="section-actions">
          <Tooltip title="Rename section">
            <IconButton size="small" onClick={() => onRenameSectionStart(section.id, section.name)}>
              <EditIcon style={{ fontSize: '1rem' }} htmlColor={colors.dotmapsGray} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete section">
            <IconButton size="small" onClick={() => onDeleteSection(section.id)}>
              <DeleteIcon style={{ fontSize: '1rem' }} htmlColor={colors.dotmapsGray} />
            </IconButton>
          </Tooltip>
          <IconButton size="small">
            <MoreVertIcon style={{ fontSize: '1rem' }} htmlColor={colors.dotmapsGray} />
          </IconButton>
          <IconButton size="small" onClick={() => onToggle(section.id)}>
            {section.collapsed
              ? <ExpandMoreIcon style={{ fontSize: '1rem' }} htmlColor={colors.dotmapsGray} />
              : <ExpandLessIcon style={{ fontSize: '1rem' }} htmlColor={colors.dotmapsGray} />
            }
          </IconButton>
        </div>
      </div>
      {!section.collapsed && (
        <Fragment>
          <div styleName="field-grid">
            {sectionFields.map(({ key, field }, fieldIndex) => (
              <FieldPreviewCard
                key={key}
                field={field}
                fieldName={key}
                index={fieldIndex}
                sectionId={section.id}
                onFieldClick={onFieldClick}
              />
            ))}
            {sectionFields.length === 0 && (
              <div styleName="empty-section-hint">No fields yet.</div>
            )}
          </div>
          <div
            styleName="add-field-row"
            onClick={() => onAddField(section.id)}
            onKeyDown={ev => ev.key === 'Enter' && onAddField(section.id)}
            role="button"
            tabIndex={0}
          >
            <AddIcon style={{ fontSize: '1rem', color: colors.dotmapsBlue }} />
            <span>Add field</span>
          </div>
        </Fragment>
      )}
    </div>
  );
};

SectionCard.propTypes = {
  activeSectionId: PropTypes.string,
  fields: PropTypes.object,
  index: PropTypes.number,
  onAddField: PropTypes.func,
  onDeleteSection: PropTypes.func,
  onFieldClick: PropTypes.func,
  onRenameSectionStart: PropTypes.func,
  onToggle: PropTypes.func,
  section: PropTypes.object
};

// ─── Rename Section Inline Dialog ────────────────────────────────────────────

const RenameSectionDialog = ({ sectionName, onConfirm, onCancel }) => {
  const [value, setValue] = useState(sectionName);
  return (
    <div styleName="rename-dialog">
      <TextField
        fullWidth
        InputLabelProps={{ shrink: true }}
        label="Section name"
        onChange={ev => setValue(ev.target.value)}
        size="small"
        value={value}
        onKeyDown={ev => {
          if (ev.key === 'Enter') {
            onConfirm(value);
          }
          if (ev.key === 'Escape') {
            onCancel();
          }
        }}
      />
      <div styleName="rename-actions">
        <Button size="small" color="primary" variant="contained" onClick={() => onConfirm(value)} disabled={!value.trim()}>
          Save
        </Button>
        <Button size="small" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

RenameSectionDialog.propTypes = {
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
  sectionName: PropTypes.string
};

// ─── TOC Panel ───────────────────────────────────────────────────────────────

const TocPanel = ({ activeSectionId, sections, onSelectSection }) => (
  <div styleName="toc-panel" data-testid="entity-form-builder-toc">
    {sections.map((section, sectionIndex) => {
      const isActive = section.id === activeSectionId;
      const isLast = sectionIndex === sections.length - 1;
      return (
        <div key={section.id} styleName="toc-item">
          <div styleName="toc-item-left">
            <div styleName={`toc-circle${isActive ? ' toc-circle-active' : ''}`}>
              {sectionIndex + 1}
            </div>
            {!isLast && <div styleName="toc-line" />}
          </div>
          <div
            styleName={`toc-label${isActive ? ' toc-label-active' : ''}`}
            onClick={() => onSelectSection(section.id)}
            onKeyDown={ev => ev.key === 'Enter' && onSelectSection(section.id)}
            role="button"
            tabIndex={0}
          >
            {section.name}
          </div>
        </div>
      );
    })}
  </div>
);

TocPanel.propTypes = {
  activeSectionId: PropTypes.string,
  onSelectSection: PropTypes.func,
  sections: PropTypes.array
};

// ─── Create Field Right Panel ────────────────────────────────────────────────

const CreateFieldPanel = ({ editingField, fields, onClose, onSave }) => {
  const { fieldName, isNew } = editingField;
  const existing = !isNew && fieldName ? fields[fieldName] : null;

  const [name, setName] = useState(existing?.label || (!isNew ? fieldName : '') || '');
  const [type, setType] = useState(existing?.type || 'CharField');
  const [description, setDescription] = useState(existing?.help_text || '');
  const [width, setWidth] = useState(existing?.width || 'full');
  const [required, setRequired] = useState(existing?.required || false);

  return (
    <div styleName="right-panel" data-testid="entity-form-builder-field-panel">
      <div styleName="right-panel-header">
        <span styleName="right-panel-title">{isNew ? 'Add Field' : 'Edit Field'}</span>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
      {!isNew && (
        <div styleName="right-panel-tabs">
          <span styleName="right-panel-tab right-panel-tab-active">Create Field</span>
        </div>
      )}
      {isNew && (
        <div styleName="right-panel-tabs">
          <span styleName="right-panel-tab right-panel-tab-active">Create Field</span>
        </div>
      )}
      <div styleName="right-panel-body">
        <div styleName="panel-row">
          <TextField
            fullWidth
            InputLabelProps={{ shrink: true }}
            label="Label *"
            onChange={ev => setName(ev.target.value)}
            size="small"
            value={name}
          />
        </div>
        <div styleName="panel-row">
          <FormControl fullWidth size="small">
            <InputLabel shrink>Type *</InputLabel>
            <Select
              disabled={!isNew}
              onChange={ev => setType(ev.target.value)}
              value={type}
            >
              {FIELD_TYPES.map(ft => (
                <MenuItem key={ft.value} value={ft.value}>{ft.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div styleName="panel-row">
          <TextField
            fullWidth
            InputLabelProps={{ shrink: true }}
            label="Description"
            multiline
            onChange={ev => setDescription(ev.target.value)}
            rows={3}
            size="small"
            value={description}
          />
        </div>
        <div styleName="panel-row">
          <FormControl fullWidth size="small">
            <InputLabel shrink>Width</InputLabel>
            <Select onChange={ev => setWidth(ev.target.value)} value={width}>
              {FIELD_WIDTHS.map(fw => (
                <MenuItem key={fw.value} value={fw.value}>{fw.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div styleName="panel-row">
          <SwitchControl label="Required field" onChange={setRequired} value={required} />
        </div>
      </div>
      <div styleName="right-panel-footer">
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="primary"
          disabled={!name.trim()}
          onClick={() => onSave({ name: name.trim(), type, description, width, required })}
          variant="contained"
        >
          Save
        </Button>
      </div>
    </div>
  );
};

CreateFieldPanel.propTypes = {
  editingField: PropTypes.object,
  fields: PropTypes.object,
  onClose: PropTypes.func,
  onSave: PropTypes.func
};

// ─── Settings Right Panel ────────────────────────────────────────────────────

const SettingsPanel = ({ entityType, onClose, onSave }) => {
  const [name, setName] = useState(entityType.label || entityType.name || '');
  const [description, setDescription] = useState(entityType.description || '');

  return (
    <div styleName="right-panel" data-testid="entity-form-builder-settings-panel">
      <div styleName="right-panel-header">
        <span styleName="right-panel-title">Settings</span>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
      <div styleName="right-panel-body">
        <div styleName="panel-row">
          <TextField
            fullWidth
            InputLabelProps={{ shrink: true }}
            label={<span>Name <span style={{ color: colors.dotmapsRed }}>*</span></span>}
            onChange={ev => setName(ev.target.value)}
            size="small"
            value={name}
          />
        </div>
        <div styleName="panel-row">
          <TextField
            fullWidth
            InputLabelProps={{ shrink: true }}
            label="Description"
            multiline
            onChange={ev => setDescription(ev.target.value)}
            rows={5}
            size="small"
            value={description}
          />
        </div>
      </div>
      <div styleName="right-panel-footer">
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="primary"
          disabled={!name.trim()}
          onClick={() => onSave({ name: name.trim(), description })}
          variant="contained"
        >
          Save
        </Button>
      </div>
    </div>
  );
};

SettingsPanel.propTypes = {
  entityType: PropTypes.object,
  onClose: PropTypes.func,
  onSave: PropTypes.func
};

// ─── Preview Mode ────────────────────────────────────────────────────────────

const PreviewMode = ({ entityType, sections, fields, activeSectionId, onSelectSection }) => {
  const activeSection = sections.find(sec => sec.id === activeSectionId) || sections[0];
  const activeIndex = sections.findIndex(sec => sec.id === (activeSection?.id));
  const sectionFields = activeSection
    ? (activeSection.fields || []).map(key => ({ key, field: fields[key] })).filter(({ field }) => Boolean(field))
    : [];

  return (
    <div styleName="preview-mode" data-testid="entity-form-builder-preview">
      <div styleName="preview-body">
        <div styleName="preview-toc">
          <TocPanel
            activeSectionId={activeSection?.id}
            sections={sections}
            onSelectSection={onSelectSection}
          />
        </div>
        <div styleName="preview-content">
          <div styleName="preview-entity-title">{entityType.label || entityType.name}</div>
          {activeSection && (
            <Fragment>
              <div styleName="preview-step">
                Step {activeIndex + 1} of {sections.length}
                <span styleName="preview-required-note">* Indicates a required field</span>
              </div>
              <div styleName="preview-section-name">{activeSection.name}</div>
              <div styleName="preview-field-grid">
                {sectionFields.map(({ key, field }) => (
                  <div
                    key={key}
                    styleName={`preview-field-card${!field.width || field.width === 'full' ? ' preview-field-full' : ' preview-field-half'}`}
                  >
                    <div styleName="field-label">
                      {field.label || key}
                      {field.required && <span styleName="required-star"> *</span>}
                    </div>
                    <FieldInputPreview field={field} />
                  </div>
                ))}
              </div>
            </Fragment>
          )}
        </div>
      </div>
    </div>
  );
};

PreviewMode.propTypes = {
  activeSectionId: PropTypes.string,
  entityType: PropTypes.object,
  fields: PropTypes.object,
  onSelectSection: PropTypes.func,
  sections: PropTypes.array
};

// ─── Main Form Builder ───────────────────────────────────────────────────────

// Internal/system fields that should never appear as user-facing form fields.
const SYSTEM_FIELDS = new Set(['id', 'type', 'checksum', 'entity_checksum', 'generates_overlaps']);

// Convert a snake_case or pipe-prefixed field name to a human-readable label.
const toReadableLabel = fieldName => {
  const name = fieldName.startsWith('|') ? fieldName.slice(1) : fieldName;
  return name
    .replace(/[-_]/g, ' ')
    .split(' ')
    .map(word => (word.length > 0 ? word[0].toUpperCase() + word.slice(1) : word))
    .join(' ');
};

// Infer a field type suitable for preview rendering from city config column data.
const inferFieldType = (fieldName, colConfig) => {
  const lowerName = fieldName.toLowerCase();
  if (lowerName.includes('date') || lowerName === 'start_date' || lowerName === 'end_date') {
    return 'DateField';
  }
  if (!colConfig) {
    return 'CharField';
  }
  if (colConfig.componentType === 'toggle' || colConfig.componentType === 'checkbox') {
    return 'BooleanField';
  }
  if (
    colConfig.data === 'map_category' ||
    colConfig.data === 'agency' ||
    colConfig.componentType === 'dropdown' ||
    colConfig.type === 'field'
  ) {
    return 'SelectField';
  }
  return 'CharField';
};

// Build a field definition object from a column config entry.
const buildFieldDef = (fieldName, col) => ({
  label: (col && col.label) || toReadableLabel(fieldName),
  type: inferFieldType(fieldName, col),
  required: (col && col.required) || false,
  width: (col && col.style === 'col50') ? 'half' : 'full',
  help_text: ''
});

// Convert city config tabs + form columns into our section/field format (read-only starting point).
const buildSectionsFromCityConfig = (tabs, columns) => {
  const builtSections = [];
  const builtFields = {};

  for (const tab of tabs) {
    if (tab.type === 'segment') {
      continue;
    }

    // Explicit tab fields (excluding specials, segments, and system fields)
    const explicitFields = (tab.fields || []).filter(
      fieldName =>
        !fieldName.startsWith('_') &&
        fieldName !== 'segments' &&
        !SYSTEM_FIELDS.has(fieldName)
    );

    // date_range_fields (start_date, end_date, all_day) live under a separate key
    const dateRangeFields = (tab.date_range_fields || []);

    const sectionFields = [...explicitFields, ...dateRangeFields];

    if (sectionFields.length === 0) {
      continue;
    }

    builtSections.push({
      id: tab.id,
      name: tab.label || toReadableLabel(tab.id),
      fields: sectionFields,
      collapsed: false
    });

    for (const fieldName of sectionFields) {
      if (!builtFields[fieldName]) {
        builtFields[fieldName] = buildFieldDef(fieldName, columns[fieldName]);
      }
    }
  }
  return { builtSections, builtFields };
};

const EntityTypeFormBuilder = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { data: entityType } = useSelector(state => state.dataDetail);
  const { details: configDetails } = useSelector(state => state.config);

  // Panel/mode state is driven by URL params so TopBar actions and this component stay in sync
  const urlParams = new URLSearchParams(location.search);
  const isPreview = urlParams.get('mode') === 'preview';
  const urlPanel = urlParams.get('panel'); // 'settings' | null

  const clearUrlParam = useCallback((param) => {
    const params = new URLSearchParams(location.search);
    params.delete(param);
    const search = params.toString();
    history.replace(search ? `${location.pathname}?${search}` : location.pathname);
  }, [history, location]);

  const [rightPanel, setRightPanel] = useState(null); // 'field' | null (settings is URL-driven)
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [sections, setSections] = useState([]);
  const [fields, setFields] = useState({});
  const [currentEntityId, setCurrentEntityId] = useState(null);
  const [renamingSection, setRenamingSection] = useState(null);

  // Derive the active right panel: URL param takes precedence over local state
  const activePanel = urlPanel || rightPanel;

  useEffect(() => {
    if (!entityType) {
      return;
    }

    // Always load labels/widths from city config — entityType.fields only has type/required, no labels.
    const entityTypeName = entityType.name;
    const configTabs = configDetails?.[entityTypeName]?.tabs || [];
    const configColumns = configDetails?.[entityTypeName]?.form?.columns || {};

    // Build a field def that merges configColumns (for label/width) with entityType.fields (for type/required).
    const resolveFieldDef = key => ({
      ...buildFieldDef(key, configColumns[key]),
      ...(entityType.fields?.[key] || {})
    });

    const savedSections = entityType.config?.sections || [];

    // Detect sections saved by the old auto-migration (single "General Information" section)
    // and treat them as if no sections exist, so we rebuild from city config.
    const isStaleAutomigration =
      savedSections.length === 1 && savedSections[0].name === 'General Information';

    const isNewEntity = entityType.id !== currentEntityId;

    if (savedSections.length > 0 && !isStaleAutomigration) {
      // User has explicitly configured sections — use the structure but enrich field defs
      // with labels from configColumns (entityType.fields has no label property).
      const allKeys = new Set(savedSections.flatMap(sec => sec.fields || []));
      const enrichedFields = {};
      for (const key of allKeys) {
        enrichedFields[key] = resolveFieldDef(key);
      }
      setFields(enrichedFields);
      if (isNewEntity) {
        // Full reset only when switching to a different entity — preserves collapsed state on re-fetches.
        setCurrentEntityId(entityType.id);
        setSections(savedSections.map(sec => ({ ...sec, collapsed: sec.collapsed ?? false })));
        setActiveSectionId(savedSections[0].id);
      }
      return;
    }

    if (!isNewEntity) {
      return;
    }

    // Everything below is first-load / entity-switch only.
    setCurrentEntityId(entityType.id);

    // Fall back to city config tabs as the display source.
    if (configTabs.length > 0) {
      const { builtSections, builtFields } = buildSectionsFromCityConfig(configTabs, configColumns);

      // Override with resolved field defs so labels + widths are always correct.
      const resolvedFields = {};
      for (const key of Object.keys(builtFields)) {
        resolvedFields[key] = resolveFieldDef(key);
      }

      setSections(builtSections);
      setFields(resolvedFields);
      if (builtSections.length > 0) {
        setActiveSectionId(builtSections[0].id);
      }
      return;
    }

    // No city config tabs — show custom fields from entityType.fields in a single section.
    const entityCustomFields = entityType.fields || {};
    const customKeys = Object.keys(entityCustomFields).filter(key => !SYSTEM_FIELDS.has(key));
    if (customKeys.length > 0) {
      const fallbackSection = { id: 'custom', name: 'Fields', fields: customKeys, collapsed: false };
      const fallbackFields = {};
      for (const key of customKeys) {
        fallbackFields[key] = resolveFieldDef(key);
      }
      setSections([fallbackSection]);
      setFields(fallbackFields);
      setActiveSectionId(fallbackSection.id);
    }
  }, [entityType, configDetails]);  // eslint-disable-line react-hooks/exhaustive-deps

  const persist = useCallback((updatedSections, updatedFields, extra = {}) => {
    if (!entityType) {
      return;
    }
    const payload = {
      config: { ...entityType.config, sections: updatedSections },
      fields: updatedFields,
      ...extra
    };
    dispatch(saveDataType('map_type', entityType.id, payload, () => {
      dispatch(fetchDataDetail('map_type', entityType.id));
    }));
  }, [dispatch, entityType]);

  const handleAddSection = useCallback(() => {
    const newSection = { id: uuid(), name: 'Untitled', fields: [], collapsed: false };
    const updated = [...sections, newSection];
    setSections(updated);
    setActiveSectionId(newSection.id);
    persist(updated, fields);
  }, [sections, fields, persist]);

  const handleToggleSection = useCallback(sectionId => {
    const updated = sections.map(sec =>
      sec.id === sectionId ? { ...sec, collapsed: !sec.collapsed } : sec
    );
    setSections(updated);
    persist(updated, fields);
  }, [sections, fields, persist]);

  const handleDeleteSection = useCallback(sectionId => {
    const updated = sections.filter(sec => sec.id !== sectionId);
    setSections(updated);
    if (activeSectionId === sectionId) {
      setActiveSectionId(updated[0]?.id || null);
    }
    persist(updated, fields);
  }, [sections, fields, activeSectionId, persist]);

  const handleRenameSectionStart = useCallback((sectionId, sectionName) => {
    setRenamingSection({ id: sectionId, name: sectionName });
  }, []);

  const handleRenameSectionConfirm = useCallback(newName => {
    if (!renamingSection || !newName.trim()) {
      return;
    }
    const updated = sections.map(sec =>
      sec.id === renamingSection.id ? { ...sec, name: newName.trim() } : sec
    );
    setSections(updated);
    setRenamingSection(null);
    persist(updated, fields);
  }, [sections, fields, renamingSection, persist]);

  const handleSelectSection = useCallback(sectionId => {
    setActiveSectionId(sectionId);
    const section = sections.find(sec => sec.id === sectionId);
    if (section?.collapsed) {
      const updated = sections.map(sec =>
        sec.id === sectionId ? { ...sec, collapsed: false } : sec
      );
      setSections(updated);
      persist(updated, fields);
    }
    const el = document.querySelector(`[data-section-id="${sectionId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [sections, fields, persist]);

  const handleAddField = useCallback(sectionId => {
    setEditingField({ fieldName: null, sectionId, isNew: true });
    setRightPanel('field');
    setActiveSectionId(sectionId);
  }, []);

  const handleFieldClick = useCallback((fieldName, sectionId) => {
    setEditingField({ fieldName, sectionId, isNew: false });
    setRightPanel('field');
    setActiveSectionId(sectionId);
  }, []);

  const handleCloseRightPanel = useCallback(() => {
    setRightPanel(null);
    setEditingField(null);
    if (urlPanel) {
      clearUrlParam('panel');
    }
  }, [urlPanel, clearUrlParam]);

  const handleSaveField = useCallback(fieldData => {
    if (!editingField) {
      return;
    }
    const { fieldName, sectionId, isNew } = editingField;
    const updatedFields = { ...fields };
    let updatedSections = sections;

    if (isNew) {
      const rawKey = fieldData.name.toLowerCase().replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
      let key = rawKey;
      let suffix = 2;
      while (updatedFields[key]) {
        key = `${rawKey}_${suffix}`;
        suffix += 1;
      }
      updatedFields[key] = {
        label: fieldData.name,
        type: fieldData.type,
        required: fieldData.required,
        help_text: fieldData.description,
        width: fieldData.width
      };
      updatedSections = sections.map(sec =>
        sec.id === sectionId ? { ...sec, fields: [...(sec.fields || []), key] } : sec
      );
    } else {
      updatedFields[fieldName] = {
        ...fields[fieldName],
        label: fieldData.name,
        required: fieldData.required,
        help_text: fieldData.description,
        width: fieldData.width
      };
    }

    setSections(updatedSections);
    setFields(updatedFields);
    setRightPanel(null);
    setEditingField(null);
    persist(updatedSections, updatedFields);
  }, [editingField, sections, fields, persist]);

  const handleSaveSettings = useCallback(settingsData => {
    if (!entityType) {
      return;
    }
    dispatch(saveDataType('map_type', entityType.id, {
      label: settingsData.name,
      display_name: settingsData.name
    }, () => {
      dispatch(fetchDataDetail('map_type', entityType.id));
      dispatch(pushApplicationMessage('Settings saved.'));
    }));
    setRightPanel(null);
    if (urlPanel) {
      clearUrlParam('panel');
    }
  }, [dispatch, entityType, urlPanel, clearUrlParam]);

  const handleReorderField = useCallback((sectionId, fieldName, fromIndex, toIndex) => {
    const updatedSections = sections.map(sec => {
      if (sec.id !== sectionId) {
        return sec;
      }
      const secFields = [...(sec.fields || [])];
      secFields.splice(fromIndex, 1);
      secFields.splice(toIndex, 0, fieldName);
      return { ...sec, fields: secFields };
    });
    setSections(updatedSections);
    persist(updatedSections, fields);
  }, [sections, fields, persist]);

  const handleReorderSection = useCallback((fromIndex, toIndex) => {
    const updated = [...sections];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    setSections(updated);
    persist(updated, fields);
  }, [sections, fields, persist]);

  // Global DnD monitor
  useEffect(() => {
    return monitorForElements({
      onDrop: ({ source, location: dropLocation }) => {
        const destination = dropLocation.current.dropTargets[0];
        if (!destination) {
          return;
        }
        if (source.data.type === 'entity-field') {
          const srcIndex = source.data.index;
          const srcSection = source.data.sectionId;
          const dstSection = destination.data.sectionId;
          if (srcSection !== dstSection) {
            return;
          }
          const edge = extractClosestEdge(destination.data);
          let dstIndex = destination.data.index;
          if (edge === 'bottom') {
            dstIndex += 1;
          }
          if (srcIndex < dstIndex) {
            dstIndex -= 1;
          }
          if (srcIndex !== dstIndex) {
            handleReorderField(srcSection, source.data.fieldName, srcIndex, dstIndex);
          }
        } else if (source.data.type === 'entity-section') {
          const srcIndex = source.data.index;
          const edge = extractClosestEdge(destination.data);
          let dstIndex = destination.data.index;
          if (edge === 'bottom') {
            dstIndex += 1;
          }
          if (srcIndex < dstIndex) {
            dstIndex -= 1;
          }
          if (srcIndex !== dstIndex) {
            handleReorderSection(srcIndex, dstIndex);
          }
        }
      }
    });
  }, [handleReorderField, handleReorderSection]);

  if (!entityType) {
    return null;
  }

  if (isPreview) {
    return (
      <PreviewMode
        activeSectionId={activeSectionId}
        entityType={entityType}
        fields={fields}
        sections={sections}
        onSelectSection={handleSelectSection}
      />

    );
  }

  return (
    <div data-testid="entity-type-form-builder" styleName="form-builder-container">
      <div styleName="body">
        {/* TOC */}
        <div styleName="toc-wrapper">
          <TocPanel
            activeSectionId={activeSectionId}
            sections={sections}
            onSelectSection={handleSelectSection}
          />
        </div>

        {/* Form Area */}
        <div styleName="form-area">
          {renamingSection && (
            <RenameSectionDialog
              sectionName={renamingSection.name}
              onConfirm={handleRenameSectionConfirm}
              onCancel={() => setRenamingSection(null)}
            />
          )}
          {sections.map((section, sectionIndex) => (
            <div key={section.id} data-section-id={section.id}>
              <SectionCard
                activeSectionId={activeSectionId}
                fields={fields}
                index={sectionIndex}
                section={section}
                onAddField={handleAddField}
                onDeleteSection={handleDeleteSection}
                onFieldClick={handleFieldClick}
                onRenameSectionStart={handleRenameSectionStart}
                onToggle={handleToggleSection}
              />
            </div>
          ))}
          <div
            styleName="add-section-row"
            onClick={handleAddSection}
            onKeyDown={ev => ev.key === 'Enter' && handleAddSection()}
            role="button"
            tabIndex={0}
          >
            <AddIcon style={{ fontSize: '1rem', color: colors.dotmapsGray }} />
            <span>Add section</span>
          </div>
        </div>

        {/* Right Panel */}
        {activePanel === 'field' && editingField && (
          <CreateFieldPanel
            editingField={editingField}
            fields={fields}
            onClose={handleCloseRightPanel}
            onSave={handleSaveField}
          />
        )}
        {activePanel === 'settings' && (
          <SettingsPanel
            entityType={entityType}
            onClose={handleCloseRightPanel}
            onSave={handleSaveSettings}
          />
        )}
      </div>
    </div>
  );
};

export default EntityTypeFormBuilder;
